// File search conveniences ported from invenire-v2 (saved-searches.ts, facets.tsx, font-size.ts, pager.tsx)
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

type ReadStorage = Pick<Storage, 'getItem'>;
type WriteStorage = Pick<Storage, 'setItem'>;

// ---- saved searches: named, per project, in localStorage ----

export const MAX_SAVED_SEARCHES = 20;

export interface SavedSearch {
  id: string;
  name: string;
  /** the search form's value (date fields hold datepicker dates) */
  form: Record<string, any>;
  taggedOnly: boolean;
  createdAt: string;
}

export const savedSearchesKey = (projectId: string) => `invenire:file-search:saved:${projectId}`;

export function readSavedSearches(storage: ReadStorage, projectId: string): SavedSearch[] {
  try {
    const parsed = JSON.parse(storage.getItem(savedSearchesKey(projectId)) ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function writeSavedSearches(storage: WriteStorage, projectId: string, searches: SavedSearch[]) {
  try {
    storage.setItem(savedSearchesKey(projectId), JSON.stringify(searches));
  } catch {
    // storage full or blocked: the list still works until the page reloads
  }
}

export const addSavedSearch = (searches: SavedSearch[], search: SavedSearch) =>
  [search, ...searches].slice(0, MAX_SAVED_SEARCHES);

// ---- A- / A+ table text size, remembered ----

export const TABLE_FONT_SIZE = { key: 'invenire:file-search:table-font-size', min: 11, max: 18, default: 13 };

export const clampTableFontSize = (size: number) =>
  Math.min(Math.max(Math.round(size), TABLE_FONT_SIZE.min), TABLE_FONT_SIZE.max);

export function readTableFontSize(storage: ReadStorage): number {
  try {
    const size = parseInt(storage.getItem(TABLE_FONT_SIZE.key), 10);
    return Number.isFinite(size) ? clampTableFontSize(size) : TABLE_FONT_SIZE.default;
  } catch {
    return TABLE_FONT_SIZE.default;
  }
}

export function writeTableFontSize(storage: WriteStorage, size: number) {
  try {
    storage.setItem(TABLE_FONT_SIZE.key, String(size));
  } catch {
    // storage full or blocked: the size still applies for this visit
  }
}

// ---- page jump ----

/** Pages of results; the search API returns at most `cap` records */
export const pageCount = (totalRecords: number, limit: number, cap = 10000) =>
  Math.ceil(Math.min(totalRecords, cap) / limit);

/** The page number typed into the jump box, or null when it isn't a page that exists */
export function parsePageJump(value: string | number | null, pages: number): number | null {
  const page = Number(value);
  return value !== null && value !== '' && Number.isInteger(page) && page >= 1 && page <= pages ? page : null;
}

// ---- quick filters ----

const MB = 1024 * 1024;

/** Size presets; each sets both bounds so presets don't combine into an empty range */
export const SIZE_RANGES = [
  { label: '< 1 MB', minSize: '', maxSize: MB },
  { label: '1-100 MB', minSize: MB, maxSize: 100 * MB },
  { label: '> 100 MB', minSize: 100 * MB, maxSize: '' },
];

export const DATE_RANGES = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last year', days: 365 },
];

export function daysAgo(days: number, today = new Date()): NgbDateStruct {
  const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() - days);
  return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
}
